import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { SparklesIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

import {
  attentionSummary,
  OverviewSection,
  type DecisionQueue,
  type OverviewFacts,
} from "../components/regulation/OverviewSection";
import { overviewNutshellSentence } from "../components/regulation/overviewNutshell";
import { InspectionActivityCard } from "../components/regulation/inspector/InspectionActivity";
import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import {
  ComplianceOrganizationMissing,
  ComplianceUnavailable,
} from "../components/regulation/ComplianceUnavailable";
import { usePolicies, useRegulatoryUpdates } from "../state/policies";
import {
  useOrganizationFindings,
  useReviewCounts,
  useOrgGovernance,
  useOwnersByPolicy,
  useRecommendations,
  useRegulationOverview,
  useRegulationTimeline,
} from "../state/governance";

/**
 * The Regulation landing page — where the organization stands, and the next
 * thing worth doing. The header above it is the workspace's, not this page's:
 * search and Probe stay put as you move between sections.
 *
 * The page reads the same org-wide snapshots the shelf does (governance,
 * owners, recommendations, findings, regulatory updates), so its numbers and
 * the shelf's can never disagree.
 */
function RegulationOverviewRoute() {
  const { org: orgSegment } = Route.useParams();
  const navigate = useNavigate();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const reviewCountsView = useReviewCounts(organizationId);
  const overviewView = useRegulationOverview(organizationId);
  const policiesView = usePolicies(organizationId);
  const timelineView = useRegulationTimeline(organizationId);
  const governanceView = useOrgGovernance(organizationId);
  const ownersView = useOwnersByPolicy(organizationId);
  const recommendationsView = useRecommendations(organizationId);
  const findingsView = useOrganizationFindings(organizationId);
  const updatesView = useRegulatoryUpdates(organizationId);
  const { snapshot } = overviewView;
  const policyList = policiesView.snapshot;
  const timeline = timelineView.snapshot;
  const governance = governanceView.byPolicyId;
  const owners = ownersView.byPolicyId;
  const recommendationList = recommendationsView.snapshot;
  const findingList = findingsView.snapshot;
  const updates = updatesView.snapshot;
  const unavailableReason =
    [
      overviewView,
      policiesView,
      timelineView,
      governanceView,
      ownersView,
      recommendationsView,
      findingsView,
      updatesView,
    ].find((view) => view.unavailableReason !== null)?.unavailableReason ?? null;
  const ready =
    snapshot !== null &&
    policyList !== null &&
    timeline !== null &&
    governanceView.snapshot !== null &&
    ownersView.ready &&
    recommendationList !== null &&
    findingList !== null &&
    updates !== null;
  const isPending = !ready && unavailableReason === null;

  const facts = useMemo<OverviewFacts | null>(() => {
    if (!ready || snapshot === null) return null;
    return {
      ...(reviewCountsView.snapshot ? { reviewCounts: reviewCountsView.snapshot } : {}),
      overview: snapshot,
      policies: policyList?.policies ?? [],
      governance,
      owners,
      recommendations: recommendationList?.recommendations ?? [],
      findings: (findingList?.findings ?? []).map((finding) => ({
        policyId: finding.policyId,
        title: finding.title,
        status: finding.status,
        severity: finding.severity,
        detectedAt: finding.detectedAt,
      })),
      touchedPolicyIds: new Set(
        (updates?.updates ?? []).flatMap((update) => update.affectedPolicyIds),
      ),
      timeline: timeline?.buckets ?? [],
      // One clock reading per snapshot, so every figure agrees on "now".
      now: Date.now(),
    };
  }, [
    ready,
    reviewCountsView.snapshot,
    snapshot,
    policyList,
    governance,
    owners,
    recommendationList,
    findingList,
    updates,
    timeline,
  ]);

  const openAll = useCallback(() => {
    void navigate({ to: "/$org/compliance/policies", params: { org: orgSegment } });
  }, [navigate, orgSegment]);

  const openQueue = useCallback(
    (queue: DecisionQueue) => {
      void navigate({
        to: queue === "findings" ? "/$org/compliance/findings" : "/$org/compliance/suggestions",
        params: { org: orgSegment },
      });
    },
    [navigate, orgSegment],
  );

  const openPolicy = useCallback(
    (policyId: string) => {
      void navigate({
        to: "/$org/compliance/policies/$policyId",
        params: { org: orgSegment, policyId },
      });
    },
    [navigate, orgSegment],
  );

  return (
    <RegulationPageShell
      title={org ? `Welcome back` : "Overview"}
      description={
        facts === null
          ? "Your organization's compliance posture, from the records you keep here."
          : attentionSummary(facts)
      }
      // The library in numbers, right under the headline: what "today you
      // have…" is measured against. Same facts as the cards, so no drift.
    >
      {() => (
        <div className="mt-5">
          {/* The library in numbers, full width above the cards: what "today
              you have…" is measured against. Same facts, so no drift. */}
          {facts !== null && facts.policies.length > 0 ? (
            <p
              aria-label="In a nutshell"
              className="mb-4 flex w-full items-start gap-2 rounded-xl border border-border/60 bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            >
              <SparklesIcon className="size-3.5 shrink-0 text-primary" aria-hidden />
              <span className="min-w-0 leading-5">
                <span className="font-medium text-foreground">In a nutshell:</span>{" "}
                {overviewNutshellSentence(facts)}
              </span>
            </p>
          ) : null}
          {organizationId === null && !isLoading ? (
            <ComplianceOrganizationMissing />
          ) : unavailableReason !== null ? (
            <ComplianceUnavailable
              reason={unavailableReason}
              onRetry={() => {
                for (const view of [
                  overviewView,
                  policiesView,
                  timelineView,
                  governanceView,
                  ownersView,
                  recommendationsView,
                  findingsView,
                  updatesView,
                ])
                  view.refresh();
              }}
            />
          ) : (
            <OverviewSection
              facts={facts}
              isPending={isPending}
              onOpenAll={openAll}
              onOpenPolicy={openPolicy}
              onOpenQueue={openQueue}
              inspectionActivity={<InspectionActivityCard organizationId={organizationId} layout="dashboard" className="lg:col-span-7"
                action={<Link to="/$org/compliance/inspector" params={{ org: orgSegment }} className="font-medium text-muted-foreground hover:text-foreground">View inspections →</Link>} />}
            />
          )}
        </div>
      )}
    </RegulationPageShell>
  );
}

export const Route = createFileRoute("/$org/compliance/")({
  component: RegulationOverviewRoute,
});
